// Cliente do Protocolo do Hábito.
// Um único endpoint governa a Home: /api/habito/hoje. Todo o resto são escritas.

import { apiFetchWithSession } from '../../services/api';
import { AuthSession } from '../../types';

const API = '/api/habito';

async function ok<T = any>(r: Response): Promise<T> {
  const text = await r.text();
  const data = text ? (() => { try { return JSON.parse(text); } catch { return {}; } })() : {};
  if (!r.ok) throw new Error((data && (data as any).error) || `HTTP ${r.status}`);
  return data as T;
}

function post(session: AuthSession | null, path: string, body?: any, method = 'POST') {
  return apiFetchWithSession(session, `${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
}

export type EstadoChave =
  | 'PRECISA_QUALIFICAR'
  | 'DISPENSADO'
  | 'LEITOR'
  | 'EXPLORANDO'
  | 'PRECISA_PREPARAR'
  | 'AGUARDANDO_JANELA'
  | 'PRONTO_PARA_LER'
  | 'DIA_VENCIDO'
  | 'RETORNO'
  | 'FORMADO';

export interface Estagio { n: number; chave: string; nome: string }

export interface LivroDoDia {
  id: string; titulo: string; autor: string;
  paginaAtual: number; totalPaginas: number; proximaPagina: number;
  percentual: number; capaUrl?: string | null; capaHue?: number; capaEstilo?: string;
  status?: string;
}

export interface EstadoHabito {
  estado: EstadoChave;
  estagio: Estagio;
  dia: number | null;
  diaMarco: number;
  // Terminou o livro e a fila esta vazia: a tela pergunta qual e o proximo.
  precisaEscolherLivro?: boolean;
  inscricao: null | {
    id: string; status: string; perfil: 'reencontro' | 'ignicao' | null;
    interesse: string | null; minimoPaginas: number; lembretes: 'auto' | 'off';
    modo: 'leitor' | 'protocolo';
    startedOn: string | null; seladoEm: string | null;
  };
  contrato: null | {
    id: string; quando: string; quandoKind: 'rotina' | 'horario' | null;
    quandoHora: string | null; onde: string; ensaiadoEm: string | null;
  };
  livro: LivroDoDia | null;
  filaTem?: number;
  // Invariante 4: a sequência nunca viaja sozinha.
  sequencia: { atual: number; melhor: number; diasLidos: number };
  diasComCheckin?: string[];
  protecoes?: { restantes: number; total: number; usadas: number; dias?: string[] };
  leuHoje?: boolean;
  lembrete?: { enviar: boolean; tom: string; cadencia?: string; motivo?: string };
  falta?: { livro: boolean; contrato: boolean; ensaio: boolean };
  hojeLido?: { paginas: number; minutos: number };
  lapso?: { id: string; dias: number; faltouDe: string; faltouAte: string; retornouEm: string | null; compaixaoVista: boolean } | null;
  retorno?: { id: string; dias: number } | null;
  // Automaticidade medida, não deduzida de dias seguidos.
  medicao?: {
    marco: number | null;
    pedir: boolean;
    ultimo: { diaPrograma: number; score: number; leitura: { nivel: string; titulo: string } | null; em: string } | null;
    historico: { dia: number; score: number }[];
  };
  // Os marcos do miolo: disparados por ACERTO acumulado, um de cada vez.
  marco?: { chave: string; dados?: any } | null;
  semanasNoLimiar?: number;
  podeFormar?: boolean;
  formaturaTipo?: 'plena' | 'marco';
  // Dias sem ler é o FATO; lapso.dias é contabilidade encurtada pela proteção.
  diasSemLer?: number;
  retornos?: { total: number; maiorSumico: number; mediaDias: number };
  // O que os números querem dizer. A régua é frequência (4/semana é o limiar
  // medido; 88% é a adesão de quem formou o hábito), nunca volume.
  diagnostico?: {
    janela: number; lidos: number; protegidos: number;
    taxa: number; porSemana: number; limiarSemanal: number; faltamPorSemana: number;
    faixa: 'abaixo' | 'no-limiar' | 'alta';
    cedoDemais: boolean;
    referencia: { formaram: number; naoFormaram: number };
    diasFracos: string[];
  } | null;
}

export interface Sugestoes {
  gatilhos: { id: string; texto: string; kind: 'rotina'; destaque?: boolean }[];
  lugares: { id: string; texto: string }[];
  interesses: { id: string; titulo: string; legenda: string }[];
  srbai: {
    enunciado: string;
    escala: { min: number; max: number; minRotulo: string; maxRotulo: string };
    itens: { id: string; texto: string }[];
  };
}

export interface OpcaoLivro {
  titulo: string; autor: string; paginas: number | null; capaUrl: string | null;
}
export interface ItemDaFila {
  id: string; livroId: string; titulo: string; autor: string;
  paginas: number | null; capaUrl: string | null; capaHue?: number; capaEstilo?: string;
}

export const habitoApi = {
  async hoje(session: AuthSession | null): Promise<EstadoHabito> {
    return ok<EstadoHabito>(await apiFetchWithSession(session, `${API}/hoje`));
  },
  async sugestoes(session: AuthSession | null): Promise<Sugestoes> {
    return ok<Sugestoes>(await apiFetchWithSession(session, `${API}/sugestoes`));
  },
  async qualificar(session: AuthSession | null, body: {
    intencao: 'forte' | 'explorando';
    perfil?: 'reencontro' | 'ignicao';
    interesse?: string;
  }) {
    return ok(await post(session, '/qualificar', body));
  },
  // Sair do protocolo sem sair do app. Reversível.
  async dispensar(session: AuthSession | null) {
    return ok(await post(session, '/dispensar'));
  },
  // A chave dos dois mundos. Não apaga nada de nenhum dos lados.
  async trocarModo(session: AuthSession | null, modo: 'leitor' | 'protocolo') {
    return ok<{ ok: true; modo: string }>(await post(session, '/modo', { modo }));
  },
  // ── O livro é do leitor ───────────────────────────────────────────────
  // Foto da capa → a IA transcreve o que está impresso → o catálogo enriquece.
  // A IA aqui não escolhe nem recomenda: ela só lê.
  async lerCapa(session: AuthSession | null, arquivo: File) {
    const fd = new FormData();
    fd.append('foto', arquivo);
    // Sem Content-Type de propósito: o browser precisa montar o boundary.
    const r = await apiFetchWithSession(session, `${API}/livro/foto`, { method: 'POST', body: fd });
    return ok<{ ok: boolean; motivo?: string; lido?: { titulo: string; autor: string; confianca: string }; opcoes: OpcaoLivro[] }>(r);
  },
  async criarLivro(session: AuthSession | null, body: OpcaoLivro & { paraFila?: boolean }) {
    return ok<{ ok: true; livroId: string }>(await post(session, '/livro/novo', body));
  },
  async buscarLivros(session: AuthSession | null, q: string) {
    const r = await apiFetchWithSession(session, `/api/books/search?q=${encodeURIComponent(q)}`);
    const d = await ok<{ results: any[] }>(r);
    return (d.results || []).map((x) => ({
      titulo: x.title, autor: x.author, paginas: x.pages, capaUrl: x.coverSourceUrl,
    })) as OpcaoLivro[];
  },
  async fila(session: AuthSession | null) {
    return ok<{ maximo: number; fila: ItemDaFila[] }>(await apiFetchWithSession(session, `${API}/fila`));
  },
  async addFila(session: AuthSession | null, bookId: string) {
    return ok(await post(session, '/fila', { bookId }));
  },
  async removerDaFila(session: AuthSession | null, id: string) {
    return ok(await post(session, `/fila/${encodeURIComponent(id)}`, undefined, 'DELETE'));
  },
  // Só `bookId`: o protocolo escolhe entre o que a pessoa JÁ TEM. O
  // `catalogoId`, que apontava para o catálogo de sugestões, saiu junto com a
  // aba de sugestões da tela de trocar.
  async escolherLivro(session: AuthSession | null, body: { bookId: string }) {
    return ok<{ ok: true; livroId: string }>(await post(session, '/livro', body));
  },
  async salvarContrato(session: AuthSession | null, body: {
    quando: string; quandoKind: 'rotina' | 'horario'; quandoHora?: string | null; onde: string; sugerido: boolean;
  }) {
    return ok(await post(session, '/contrato', body));
  },
  async ensaiar(session: AuthSession | null) {
    return ok(await post(session, '/ensaio'));
  },
  async marcarMarcoVisto(session: AuthSession | null, chave: string) {
    return ok(await post(session, `/marco/${encodeURIComponent(chave)}/visto`));
  },
  async marcarLapsoVisto(session: AuthSession | null, id: string, tipo: 'compaixao' | 'celebracao') {
    return ok(await post(session, `/lapso/${encodeURIComponent(id)}/visto`, { tipo }));
  },
  async responderSrbai(session: AuthSession | null, r: { q1: number; q2: number; q3: number; q4: number }) {
    return ok(await post(session, '/srbai', r));
  },
  // Encerrar o programa EXIGE escolher o que vem depois — depois de um marco
  // entregue a atividade cai, então o próximo passo é encadeado antes do troféu.
  async formar(session: AuthSession | null, proximo: 'manutencao' | 'novo-livro' | 'mais-paginas') {
    return ok(await post(session, '/formar', { proximo }));
  },
  async ajustes(session: AuthSession | null, body: { minimoPaginas?: number; lembretes?: 'auto' | 'off'; livroAtual?: string }) {
    return ok(await post(session, '/ajustes', body, 'PATCH'));
  },
};
